import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Palette, Check } from 'lucide-react';
import { useTheme, type ThemeName, type ColorScheme } from '../../contexts/ThemeContext';
import { t } from '../../i18n/t';
import './ThemeSwitcher.css';

// TopBar theme picker. Icon-only button → dropdown listing the 3 themes,
// each with a small swatch in that theme's primary color. The dropdown
// uses absolute positioning anchored to the button (no portal — the
// topbar has no overflow:hidden, verified). Closes on outside-click /
// Escape / theme selection. Active theme has a check mark.

// Swatch color = the theme's --color-primary as resolved by tokens.css.
// Hard-coded here (rather than reading computed CSS) so each row shows
// its color regardless of the currently-active theme.
const SWATCH_COLOR: Record<ThemeName, string> = {
  lop: '#2563eb',
  mizrahi: '#cc4a23',
  discount: '#006a4e',
};

const SCHEME_LABEL_KEY = {
  light: 'theme.schemeLight',
  dark: 'theme.schemeDark',
  system: 'theme.schemeSystem',
} as const;
// Display order requested by product: light, dark, follow-the-computer.
const SCHEME_ORDER: readonly ColorScheme[] = ['light', 'dark', 'system'];

export function ThemeSwitcher() {
  const { theme, setTheme, themes, colorScheme, setColorScheme } = useTheme();
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  const close = useCallback(() => setOpen(false), []);

  useEffect(() => {
    if (!open) return;
    const handleMouseDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        close();
      }
    };
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    document.addEventListener('mousedown', handleMouseDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleMouseDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open, close]);

  const handlePick = (next: ThemeName) => {
    setTheme(next);
    close();
  };

  const handlePickScheme = (next: ColorScheme) => {
    setColorScheme(next);
    close();
  };

  return (
    <div className="theme-switcher" ref={rootRef}>
      <button
        type="button"
        className="theme-switcher__button"
        onClick={() => setOpen((p) => !p)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={t('theme.buttonAriaLabel')}
        title={t('theme.buttonAriaLabel')}
      >
        <Palette size={18} aria-hidden="true" />
      </button>
      {open && (
        <ul
          className="theme-switcher__menu"
          role="menu"
          aria-label={t('theme.menuAriaLabel')}
        >
          <li role="none">
            <ul role="group" aria-label={t('theme.themeGroupLabel')} className="theme-switcher__group">
              <li role="presentation" className="theme-switcher__group-title" aria-hidden="true">
                {t('theme.themeGroupLabel')}
              </li>
            {themes.map((name) => (
              <li key={name} role="none">
                <button
                  type="button"
                  className="theme-switcher__item"
                  role="menuitemradio"
                  aria-checked={theme === name}
                  onClick={() => handlePick(name)}
                >
                  <span
                    className="theme-switcher__swatch"
                    style={{ background: SWATCH_COLOR[name] }}
                    aria-hidden="true"
                  />
                  <span className="theme-switcher__label">{t(`theme.${name}` as const)}</span>
                  <span className="theme-switcher__check" aria-hidden="true">
                    {theme === name && <Check size={14} />}
                  </span>
                </button>
              </li>
            ))}
            </ul>
          </li>
          <li role="separator" className="theme-switcher__sep" />
          <li role="none">
            <ul role="group" aria-label={t('theme.schemeGroupLabel')} className="theme-switcher__group">
              <li role="presentation" className="theme-switcher__group-title" aria-hidden="true">
                {t('theme.schemeGroupLabel')}
              </li>
              {SCHEME_ORDER.map((name) => (
                <li key={name} role="none">
                  <button
                    type="button"
                    className="theme-switcher__item"
                    role="menuitemradio"
                    aria-checked={colorScheme === name}
                    onClick={() => handlePickScheme(name)}
                  >
                    <span className="theme-switcher__label">{t(SCHEME_LABEL_KEY[name])}</span>
                    <span className="theme-switcher__check" aria-hidden="true">
                      {colorScheme === name && <Check size={14} />}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          </li>
        </ul>
      )}
    </div>
  );
}
